<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'coachingOverview' }"
          marginBottom="5"/>

      <h6 class="element-header">Übersicht</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">Einzelcoaching Anfrage</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :unemployedDropdown="unemployedDropdown"
            :operationTypeDropdown="operationTypeDropdown"
            :officeDropdown="officeDropdown"
            :coachDropdown="coachDropdown"
            :statusDropdown="statusFiltered"
            @search="getInquiryList"/>

        <PerPage
            :values="[10, 25, 50, 100]"
            @update:perPage="getInquiryList"/>

        <div class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th class="text-center">Kunde</th>
              <th>Status</th>
              <th>Maßnahmenart</th>
              <th>JC/AA Nr</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Standort</th>
              <th>Coach</th>
              <th>Sachbearbeiter</th>
              <th>Start</th>
              <th>Ende</th>
              <th>Stand</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="coaching in inquiryList.data" :key="coaching.id">
              <td>
                <button class="btn btn-primary" @click="router.push({
                  name: 'unemployedPrivateCoachingList', params: {
                    id: Number(coaching.unemployed.id )
                  } })">
                  Kunde
                </button>
              </td>
              <td>{{ coaching?.statusTranslated }}</td>
              <td>{{ coaching?.nameOfCoaching?.operationTypeTranslated }}</td>
              <td>{{ coaching?.unemployed?.aaNumber }}</td>
              <td>{{ coaching?.unemployed?.firstName }}</td>
              <td>{{ coaching?.unemployed?.lastName }}</td>
              <td>
                <template v-if="coaching.type === 'regular'">
                  {{ coaching?.unemployed?.location?.name }}
                </template>
                <template v-else>
                  <template v-for="(office, index) in coaching.offices" :key="index">
                    {{ office?.office?.name }}
                  </template>
                </template>
              </td>
              <td>
                <template v-for="(coach, index) in coaching.coaches" :key="index">
                  {{ coach?.firstName }} {{ coach?.lastName }}<span
                    v-if="index !== coaching.coaches.length - 1">,&nbsp;</span>
                </template>
              </td>
              <td>{{ coaching.clerk }}</td>
              <td>{{ coaching.startDate }}</td>
              <td>{{ coaching.endDate }}</td>
              <td>{{ coaching.unitsDone }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="inquiryList?.pagination?.total_pages > 1"
            :totalPages="inquiryList?.pagination?.total_pages"
            :currentPage="inquiryList?.pagination?.current_page"
            :totalItems="inquiryList?.pagination?.total_items"
            :perPage="inquiryList?.pagination?.items_per_page"
            @pagechanged="getInquiryList"/>
      </div>
    </div>
  </PageTemplate>
</template>


<script setup lang="ts">
import overviewCoachingService from '@/api/CoachingOverview/PrivateCoaching';
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import router from '@/router';
// @ts-ignore
import {getFinishedUnemployedDropdown, getOfficeByUserRoleDropdown, getUserDropdown} from '@/shared/dropdowns';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/CoachingOverview/PrivateCoaching/Inquiry/Search.vue';
import {computed, onMounted, ref} from 'vue';
import {ICoachingOverviewList, IPagination} from "@/interfaces/CoachingOverview/PrivateCoaching/ICoachingOverview";
import {ICoachingOverviewFilters} from "@/interfaces/CoachingOverview/ICoachingOverviewFIlters";
import BackButton from "@/components/button/BackButton.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const unemployedDropdown = ref([]);
const coachDropdown = ref([]);
const operationTypeDropdown = ref([]);
const officeDropdown = ref([]);
const isSearching = ref(false);
const isLoaded = ref(false);

const inquiryList = ref<ICoachingOverviewList>({
  data: [],
  pagination: {
    current_page: 1,
    items_per_page: 10,
    total_pages: 0,
  } as IPagination,
});

const statusFiltered = computed(() => {
  return piniaEnumsState.enums?.coachingStatus?.filter((status: { value: string }) =>
      status.value === 'in_allocation' ||
      status.value === 'accepted' ||
      status.value === 'approval_pending'
  );
});

const filters = ref<ICoachingOverviewFilters>({
  unemployedId: '',
  coachId: '',
  officeId: '',
  operationType: '',
  startDate: '',
  endDate: '',
  status: '',
  page: 1,
  limit: 10,
  sortBy: 'firstName',
  searchTerm: '',
  orderBy: 'ASC',
});

async function getInquiryList(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: ICoachingOverviewFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = inquiryList.value?.pagination?.items_per_page;

  try {
    const {data} = await overviewCoachingService.getInquiryOverviewList({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes,
    });

    inquiryList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getOperationTypeDropdown() {
  operationTypeDropdown.value = getEnums?.value?.privateCoachingOperationTypes || [];
}

onMounted(async () => {
  await Promise.all([
    getInquiryList(),
    getFinishedUnemployedDropdown(unemployedDropdown),
    getUserDropdown(coachDropdown),
    getOfficeByUserRoleDropdown(officeDropdown),
    getOperationTypeDropdown(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
/* Add cursor pointer */
.stand_popover {
  cursor: pointer;
}
</style>